import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { X, ChevronDown } from "lucide-react";

export const PAINS = [
  { pain: "平日住房低落", fix: "平日專案＋精準廣告，每月比去年同期多 69–185 間夜" },
  { pain: "旺日住房率只有假日的一半", fix: "旺日專案價＋訂房網站加碼" },
  { pain: "網路聲量比同規格飯店少", fix: "精準曝光 ×20、UGC ×3" },
  { pain: "團建業績不如剛開幕", note: "桃園是全台工廠密度最高的縣市之一", fix: "團建詢問 2 倍以上，業績 +8%～25%" },
  { pain: "Google 有排名，但不是第一", note: "桃園 親子飯店／渡假飯店", fix: "官網人客多專案頁＋SEO 往前" },
  { pain: "畫面視覺素材少", fix: "10 組網紅影片與照片，一次補齊" },
];

const CHIPS = ["前置投入 0 元", "10 組網紅住房", "配合 4 件事", "直接成交 25% 抽成"];

const PainCard = ({ p, i, progress }) => {
  const start = 0.17 + i * 0.035;
  const opacity = useTransform(progress, [start, start + 0.04], [0, 1]);
  const y = useTransform(progress, [start, start + 0.04], [40, 0]);
  const fix = useTransform(progress, [0.42, 0.48], [0, 1]);
  return (
    <motion.div style={{ opacity, y }} data-testid={`pain-${i}`} className="rounded-2xl border border-[var(--line)] bg-white p-3 md:p-5">
      <p className="flex items-start gap-2 text-sm font-black text-[var(--ink)] md:text-base">
        <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[var(--ink)] text-white md:h-5 md:w-5"><X size={10} /></span>{p.pain}
      </p>
      {p.note && <p className="ml-6 mt-1 hidden text-xs text-[var(--muted)] md:block md:ml-7">{p.note}</p>}
      <motion.p style={{ opacity: fix }} className="ml-6 mt-2 text-xs font-bold text-[var(--orange)] md:ml-7 md:text-sm">→ {p.fix}</motion.p>
    </motion.div>
  );
};

const Headline = ({ progress }) => {
  const opacity = useTransform(progress, [0, 0.08, 0.15], [1, 1, 0]);
  const y = useTransform(progress, [0.08, 0.15], [0, -80]);
  const hint = useTransform(progress, [0, 0.05], [1, 0]);
  return (
    <motion.div style={{ opacity, y }} className="absolute inset-0 flex flex-col justify-center px-5">
      <div className="mx-auto w-full max-w-6xl">
        <p className="text-xs font-bold tracking-widest text-[var(--muted)] md:text-sm">人客多多間飯店與包棟民宿的實戰累積，直接實操</p>
        <h1 data-testid="plan-name" className="mt-4 whitespace-nowrap font-black leading-none text-[var(--ink)]">
          <span className="flex items-center gap-4 md:gap-6">
            <img src="/assets/logo.webp" alt="Lakeze 人客多" data-testid="title-logo" className="block h-[10vw] w-auto md:h-16" />
            <span className="text-[6vw] font-light text-[var(--muted)] md:text-4xl">×</span>
            <img src="/assets/fh-logo.svg" alt="名人堂花園大飯店" data-testid="title-hotel-logo" className="block h-[5vw] w-auto md:h-10" />
          </span>
          <span className="mt-4 block text-[14vw] md:text-9xl">零元<span className="text-[var(--orange)]">滿房</span>計畫</span>
        </h1>
        <h2 data-testid="hero-title" className="mt-6 text-2xl font-black text-[var(--ink)] md:text-5xl">
          看完影片，<span className="whitespace-nowrap">直接訂房。</span>
        </h2>
        <p data-testid="hero-subtitle" className="mt-4 max-w-3xl text-base text-[var(--text)] md:text-xl">
          用網紅影片和精準廣告，帶客人從 <b className="text-[var(--ink)]">FB</b> 一路走到 <b className="text-[var(--ink)]">LINE</b>、官網，最後訂房。
        </p>
      </div>
      <motion.p style={{ opacity: hint }} className="absolute bottom-10 left-1/2 flex -translate-x-1/2 flex-col items-center text-xs tracking-[0.3em] text-[var(--muted)]">
        向下捲動<ChevronDown size={18} className="mt-1 animate-bounce" />
      </motion.p>
    </motion.div>
  );
};

const PainsScene = ({ progress }) => {
  const opacity = useTransform(progress, [0.14, 0.18, 0.54, 0.6], [0, 1, 1, 0]);
  const scale = useTransform(progress, [0.54, 0.6], [1, 0.92]);
  return (
    <motion.div style={{ opacity, scale }} className="absolute inset-0 flex flex-col justify-center px-5 pt-14">
      <div className="mx-auto w-full max-w-6xl">
        <p className="text-sm font-black tracking-widest text-[var(--muted)]">名人堂現在卡在這 6 件事</p>
        <div className="mt-4 grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-3">
          {PAINS.map((p, i) => <PainCard key={p.pain} p={p} i={i} progress={progress} />)}
        </div>
      </div>
    </motion.div>
  );
};

const SolveScene = ({ progress }) => {
  const [top, setTop] = useState(106);
  const count = useTransform(progress, [0.76, 0.88], [106, 503]);
  useMotionValueEvent(count, "change", (v) => setTop(Math.round(v)));
  const clip = useTransform(progress, [0.56, 0.68], ["circle(0% at 50% 55%)", "circle(150% at 50% 55%)"]);
  const titleScale = useTransform(progress, [0.62, 0.7], [1.8, 1]);
  const titleOpacity = useTransform(progress, [0.62, 0.68], [0, 1]);
  const l1 = useTransform(progress, [0.7, 0.75], [0, 1]);
  const l2 = useTransform(progress, [0.75, 0.8], [0, 1]);
  const l2y = useTransform(progress, [0.75, 0.8], [40, 0]);
  const rest = useTransform(progress, [0.86, 0.92], [0, 1]);
  return (
    <motion.div style={{ clipPath: clip }} data-testid="solve-all" className="absolute inset-0 flex flex-col justify-center bg-[var(--orange)] px-5 pt-14 text-white">
      <div className="mx-auto w-full max-w-6xl">
        <motion.p style={{ scale: titleScale, opacity: titleOpacity }} className="origin-left text-6xl font-black md:text-9xl">一次解決</motion.p>
        <motion.p style={{ opacity: l1 }} className="mt-6 text-2xl font-black md:text-5xl">名人堂不用先花錢，</motion.p>
        <motion.p style={{ opacity: l2, y: l2y }} className="mt-2 text-2xl font-black md:text-5xl">
          每月多 <span data-testid="solve-all-total" className="mt-1 block whitespace-nowrap text-5xl underline decoration-white/40 underline-offset-8 md:inline md:text-8xl">NT$106–{top} 萬</span>
        </motion.p>
        <motion.p style={{ opacity: l2 }} data-testid="solve-all-yoy" className="mt-3 text-sm font-bold text-white/90 md:text-lg">＊與去年同期相比</motion.p>
        <motion.div style={{ opacity: rest }}>
          <p className="mt-6 text-sm text-white/90 md:text-lg">訂房直接收益 24–111 萬＋外溢效益 82–392 萬（團建、訂房網站、Google）</p>
          <div className="mt-5 flex flex-wrap gap-2 text-sm font-black">
            {CHIPS.map((t) => <span key={t} className="rounded-full bg-white px-4 py-2 text-[var(--orange)]">{t}</span>)}
          </div>
          <p data-testid="solve-all-commission-note" className="mt-3 text-xs text-white/80">抽成只算直接效應：LINE@ 成交、人客多專案成交、電話訂房專案成交；外溢效益不抽成。</p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export const OpeningScroll = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  return (
    <div ref={ref} data-testid="opening-scroll" className="relative h-[480vh]">
      <div className="sticky top-0 h-screen overflow-hidden bg-[var(--warm)]">
        
        <Headline progress={scrollYProgress} />
        <PainsScene progress={scrollYProgress} />
        <SolveScene progress={scrollYProgress} />
      </div>
    </div>
  );
};
